import { useEffect, useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ComicCard from "@/components/feature/ComicCard";
import SectionHeader from "@/components/feature/SectionHeader";
import { getComic, comics, comicsByCategory } from "@/mocks/comics";
import { getCategory } from "@/mocks/categories";

export default function ComicDetailPage() {
  const { id } = useParams();
  const comic = getComic(id ?? "");

  const related = useMemo(() => {
    if (!comic) return [];
    return comicsByCategory(comic.category).filter((c) => c.id !== comic.id).slice(0, 4);
  }, [comic]);

  useEffect(() => {
    if (comic) {
      document.title = `${comic.title} 韩国妖精漫画${comic.short} - 妖精漫画堂`;
    }
  }, [comic]);

  if (!comic) {
    return <Navigate to="/" replace />;
  }

  const cat = getCategory(comic.category);
  const schema = {
    "@context": "https://schema.org",
    "@type": "ComicStory",
    name: comic.title,
    description: comic.short,
    author: { "@type": "Person", name: comic.author },
    genre: comic.tags,
    inLanguage: "zh-CN",
    aggregateRating: { "@type": "AggregateRating", ratingValue: comic.rating, bestRating: "10" },
    isPartOf: { "@type": "BookSeries", name: `${cat?.name}妖精漫画` },
  };

  return (
    <>
      <section className="border-b border-background-200 bg-background-100 py-10 md:py-14">
        <div className="container-site">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: cat?.name ?? "", to: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />
          <div className="grid gap-8 md:grid-cols-[240px_1fr] lg:grid-cols-[260px_1fr]">
            <div className="mx-auto w-full max-w-[240px] md:mx-0">
              <div className="relative overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <img src={comic.cover} alt={`${comic.title} ${comic.short} 漫画封面`} className="w-full object-cover object-top" />
                <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-medium text-background-50 ${comic.status === "连载中" ? "bg-accent-500" : "bg-foreground-900/80"}`}>
                  {comic.status}
                </span>
              </div>
              <div className="mt-4 flex items-center justify-center gap-5 text-center text-foreground-600">
                <div>
                  <p className="font-heading text-xl font-bold text-accent-600">{comic.rating.toFixed(1)}</p>
                  <p className="text-xs text-foreground-500">综合评分</p>
                </div>
                <div className="h-8 w-px bg-background-300" />
                <div>
                  <p className="font-heading text-xl font-bold text-foreground-900">{comic.views}</p>
                  <p className="text-xs text-foreground-500">累计热度</p>
                </div>
              </div>
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="chip">
                  <i className="ri-sparkling-2-fill text-accent-500" />
                  {cat?.name}
                </span>
                {comic.tags.map((t) => (
                  <span key={t} className="chip">{t}</span>
                ))}
              </div>
              <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">{comic.title}</h1>
              <p className="mt-3 text-base leading-relaxed text-foreground-600">{comic.short}</p>

              <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-lg bg-background-50 p-3">
                  <dt className="text-xs text-foreground-500">作者</dt>
                  <dd className="mt-1 truncate text-sm font-medium text-foreground-900">{comic.author}</dd>
                </div>
                <div className="rounded-lg bg-background-50 p-3">
                  <dt className="text-xs text-foreground-500">年份</dt>
                  <dd className="mt-1 text-sm font-medium text-foreground-900">{comic.year}</dd>
                </div>
                <div className="rounded-lg bg-background-50 p-3">
                  <dt className="text-xs text-foreground-500">连载状态</dt>
                  <dd className="mt-1 text-sm font-medium text-primary-700">{comic.status}</dd>
                </div>
                <div className="rounded-lg bg-background-50 p-3">
                  <dt className="text-xs text-foreground-500">章节数</dt>
                  <dd className="mt-1 text-sm font-medium text-foreground-900">{comic.chapters} 话</dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button className="btn btn-primary flex-1 py-3">
                  <i className="ri-book-open-line" />
                  开始阅读
                </button>
                <button className="btn btn-accent flex-1 py-3">
                  <i className="ri-bookmark-line" />
                  加入追更
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container-site grid gap-10 lg:grid-cols-[1fr_290px]">
          <div>
            <article>
              <h2 className="font-heading text-2xl font-bold text-foreground-950">作品剧情简介</h2>
              <div className="mt-4 space-y-4 rounded-lg border border-background-200 bg-background-50 p-6">
                <p className="text-sm leading-7 text-foreground-700">{comic.synopsis}</p>
                <p className="text-sm leading-7 text-foreground-700">
                  作为{cat?.name}题材的代表作之一，《{comic.title}》在{comic.tags.slice(0, 2).join("、")}等元素的交织下，构建出一个完整而迷人的奇幻世界。作者{comic.author}以细腻的笔触与扎实的剧情设计，将世界观设定与角色成长融为一体，深受广大幻想爱好者喜爱。
                </p>
              </div>
            </article>

            <section className="mt-12">
              <SectionHeader title="同题材推荐" to={`/category/${comic.category}`} linkText="更多同题材" />
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {related.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </section>
          </div>

          <aside className="space-y-8">
            <div className="card p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-900">阅读小贴士</h3>
              <ul className="mt-4 space-y-3 text-sm text-foreground-600">
                <li className="flex gap-2.5"><i className="ri-check-line mt-0.5 text-primary-600" />本站提供分类浏览与剧情简介，在线阅读请通过官方渠道。</li>
                <li className="flex gap-2.5"><i className="ri-check-line mt-0.5 text-primary-600" />连载状态以作品详情页标注与官方平台为准。</li>
                <li className="flex gap-2.5"><i className="ri-check-line mt-0.5 text-primary-600" />想了解相关世界观解读，可阅读题材解析专题。</li>
              </ul>
            </div>
            <div className="card p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-900">你可能还喜欢</h3>
              <ul className="mt-4 space-y-4">
                {comics.filter((c) => c.id !== comic.id).slice(0, 3).map((c) => (
                  <li key={c.id}>
                    <Link to={`/comic/${c.id}`} className="group flex items-center gap-3">
                      <img src={c.cover} alt={`${c.title} 漫画封面`} loading="lazy" className="h-16 w-11 shrink-0 rounded object-cover object-top" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground-800 group-hover:text-primary-700">{c.title}</p>
                        <p className="mt-0.5 flex items-center gap-1 text-xs text-accent-600"><i className="ri-star-fill text-accent-500" />{c.rating.toFixed(1)} · {c.chapters} 话</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </>
  );
}